import { lazy, Suspense, useState } from "react";
import { LayoutGrid, Map as MapIcon } from "lucide-react";
import { ShareButtons } from "@/components/site/ShareButtons";
import { IMAGES } from "@/lib/api";
import { ArmBar, ArmIntroMark } from "@/components/site/Chrome";
import { ContactSection } from "@/components/site/Contact";
import { ProjectCard, Odometer } from "@/components/site/Cards";
import { useCollection, usePageMeta } from "@/hooks/useData";

const FILTERS = ["All", "Completed", "Ongoing", "Upcoming"];
const PropertyMap = lazy(() => import("@/components/site/PropertyMap").then((m) => ({ default: m.PropertyMap })));

export default function Development() {
  usePageMeta("Real Estate Development Projects in Nigeria | Grey Wolfe",
    "Grey Wolfe Development: completed, ongoing and upcoming real estate development projects. We've delivered real projects on time: come and see them.",
    "https://www.greywolfeng.com/api/share/page/development/image.jpg");
  const items = useCollection("projects");
  const [filter, setFilter] = useState("All");
  const [view, setView] = useState("grid");
  const list = (items || []).filter((p) => filter === "All" || p.status === filter);

  return (
    <div className="page-in">
      <section className="bg-gw-deep text-white pt-24 md:pt-32 border-t-8 border-white" data-testid="development-page">
        <div className="max-w-6xl mx-auto px-6">
          <ArmBar current="development" />
          <ArmIntroMark arm="development" />
          <h1 className="lux gw-h1 mt-10">
            <span className="ln"><span>On time. On site.</span></span>
            <span className="ln"><span>Visit.</span></span>
          </h1>
          <p className="mt-6 gw-body max-w-xl" data-testid="development-differentiator">We've delivered real projects on time: come and see them.</p>
          <ShareButtons kind="page" id="development" text="Grey Wolfe Development — projects delivered on time" className="mt-8" testPrefix="development-share" />
        </div>
        <figure className="mt-12 aspect-[21/9] bg-gw-slide1 overflow-hidden m-0">
          <img src={IMAGES.development} alt="Completed residential development" className="w-full h-full object-cover" />
        </figure>
      </section>

      <section id="projects" className="bg-gw-ground text-white py-24 md:py-32 scroll-mt-16" data-testid="projects-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-8 mb-12">
            <div>
              <p className="lab text-gw-mist">Grey Wolfe Development</p>
              <h2 className="lux gw-h2 mt-4">Latest projects</h2>
            </div>
            {items && (
              <div className="text-right">
                <Odometer value={items.length} />
                <p className="lab text-gw-mist">Projects on this page</p>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mb-12 items-center">
            <div className="flex border border-white/40 mr-2" data-testid="projects-view-toggle">
              {[["grid", "Grid", LayoutGrid], ["map", "Map", MapIcon]].map(([v, label, Icon]) => (
                <button key={v} type="button" onClick={() => setView(v)} data-testid={`projects-view-${v}`} aria-pressed={view === v}
                  className={`lab px-4 py-2 inline-flex items-center gap-2 transition-colors duration-200 ${view === v ? "bg-white text-gw-onwhite" : "hover:bg-white/10"}`}>
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
            {FILTERS.map((f) => (
              <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`projects-filter-${f.toLowerCase()}`}
                className={`lab px-4 py-2 border transition-colors duration-200 ${filter === f ? "bg-white text-gw-ground border-white" : "border-white/40 hover:bg-white/10"}`}>
                {f}
              </button>
            ))}
          </div>
          {items === null ? (
            <div className="grid md:grid-cols-2 gap-12">{[0, 1].map((i) => <div key={i} className="aspect-[4/3] bg-gw-mid animate-pulse" />)}</div>
          ) : list.length === 0 ? (
            <p className="gw-body text-gw-pale" data-testid="projects-empty">No projects in this category yet — ask us about what's next.</p>
          ) : view === "map" ? (
            <Suspense fallback={<div className="h-[560px] bg-gw-mid animate-pulse" />}>
              <PropertyMap items={list} basePath="/development" cta="View project & book a visit" testId="projects-map" />
            </Suspense>
          ) : (
            <div className="grid md:grid-cols-2 gap-12" data-testid="projects-grid">
              {list.map((p, i) => <ProjectCard key={p.id} p={p} idx={i} />)}
            </div>
          )}
        </div>
      </section>
      <ContactSection defaultCompany="Grey Wolfe Development" />
    </div>
  );
}
